import React from "react";
import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { useI18n } from "@/context/i18n";

import type { TProductFormData } from "../../products.schema";
import {
  calculateNetWithGatewayCosts,
  formatProductPriceBrl,
} from "../../utils/soulhub-gateway-pricing.util";

export interface PlanPricingFieldsProps {
  form: UseFormReturn<TProductFormData>;
  cfgPrefix: string;
  /** Caminho do plano para sale_price, ex.: `capital_ranges.0` */
  fieldBase: string;
  /** false no fluxo preço comissionado (sem custo calculado via gateway). */
  applyGatewayCost?: boolean;
}

export const PlanPricingFields: React.FC<PlanPricingFieldsProps> = ({
  form,
  cfgPrefix,
  fieldBase,
  applyGatewayCost = true,
}) => {
  const { t } = useI18n();

  const netPrice = form.watch(`${cfgPrefix}.net_price` as any);
  const gatewayPercent = form.watch(`${cfgPrefix}.gateway_fee_percent` as any);
  const gatewayPerSale = form.watch(`${cfgPrefix}.gateway_fee_per_sale` as any);
  const productMin = form.watch(`${cfgPrefix}.minimum_price` as any);
  const productMax = form.watch(`${cfgPrefix}.maximum_price` as any);

  const calculatedCost = React.useMemo(
    () =>
      calculateNetWithGatewayCosts(
        Number(netPrice ?? 0),
        Number(gatewayPercent ?? 0),
        Number(gatewayPerSale ?? 0),
      ),
    [netPrice, gatewayPercent, gatewayPerSale],
  );

  const maxLabel =
    productMax === null || productMax === undefined
      ? t("products.gateway.plan.unlimited_max")
      : formatProductPriceBrl(Number(productMax ?? 0));

  return (
    <div className="space-y-4 rounded-md border border-dashed p-3 bg-muted/20">
      <p className="text-xs text-muted-foreground">
        {t("products.pricing.plan_limits_from_product")}
      </p>
      <div
        className={
          applyGatewayCost
            ? "grid grid-cols-1 md:grid-cols-3 gap-4"
            : "grid grid-cols-1 md:grid-cols-2 gap-4"
        }
      >
        {applyGatewayCost ? (
          <FormItem>
            <FormLabel>{t("products.pricing.plan_cost_calculated")}</FormLabel>
            <FormControl>
              <Input
                readOnly
                className="bg-muted"
                value={formatProductPriceBrl(calculatedCost)}
              />
            </FormControl>
          </FormItem>
        ) : null}
        <FormItem>
          <FormLabel>{t("products.pricing.minimum_price_product")}</FormLabel>
          <FormControl>
            <Input
              readOnly
              className="bg-muted"
              value={formatProductPriceBrl(Number(productMin ?? 0))}
            />
          </FormControl>
        </FormItem>
        <FormItem>
          <FormLabel>{t("products.pricing.maximum_price_product")}</FormLabel>
          <FormControl>
            <Input readOnly className="bg-muted" value={maxLabel} />
          </FormControl>
        </FormItem>
      </div>
      <FormField
        control={form.control}
        name={`${fieldBase}.sale_price`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("products.pricing.sale_price_plan")}</FormLabel>
            <FormControl>
              <RealCurrencyInput
                value={Number(field.value ?? 0)}
                onChange={field.onChange}
              />
            </FormControl>
            <p className="text-xs text-muted-foreground">
              {t("products.pricing.plan_sale_within_limits")}
            </p>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
